import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "login-loading"
}
const _hoisted_2 = {
  key: 1,
  class: "password-expired"
}
const _hoisted_3 = {
  key: 2,
  class: "password-invalidated"
}
const _hoisted_4 = ["disabled", "placeholder", "value"]
const _hoisted_5 = ["disabled", "placeholder"]
const _hoisted_6 = ["value", "disabled"]
const _hoisted_7 = { class: "status" }
const _hoisted_8 = ["action"]
const _hoisted_9 = ["value"]
const _hoisted_10 = ["value"]
const _hoisted_11 = ["value"]

import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from '@/store';
import { getBestMatchingLocale } from '@/locale';
import Loader from '@pf/vue3/Loader.vue';
import Bubble from '../components/Bubble.vue';
import axios from 'axios';


interface LoginValidateRequest
{
    JWT: string;
    App: number;
    AppSwitch: boolean;
}

interface LoginValidateResponse
{
    Renewed?: string;
    ACSUrl: string;
    App: number;
}


interface LoginRequest
{
    Username: string;
    Password: string;
    App: number;
    Locale?: string;
}


interface LoginResponse
{
    JWT: string;
    ACSUrl: string;
    App: number;
    WachtwoordVerlopen: boolean;
    WachtwoordOngeldig: boolean;
    CheckMFA: boolean;
}

// Special constructie voor tonen vertaling van error zie https://github.com/intlify/vue-i18n-next/issues/1396

export default /*@__PURE__*/_defineComponent({
  __name: 'Login',
  setup(__props) {

const globalI18n = useI18n({ useScope: 'global' });
const i18n = useI18n();
const { t } = i18n;

const router = useRouter();
const route = useRoute();
const store = useStore();

const acsForm = ref<HTMLFormElement | null>(null);
const usernameInput = ref<HTMLInputElement | null>(null);
const passwordInput = ref<HTMLInputElement | null>(null);

const validatingExisting = ref(true);
const password = ref('');
const validating = ref(false);
const attemptedLogin = ref(false);

const errorMessage = ref<string | null>(null);
const translatedMessage = computed(() => translateErrorMessage(errorMessage.value) )
const passwordExpired = ref(false);
const passwordInvalidated = ref(false);

const acsPostData = ref({
    action: '',
    jwt: '' as string | null
});


function translateErrorMessage(value: string | null | undefined) {
    switch (value) {
        case 'nousernamepassword': return computed(_ => t('error.nousernamepassword')) 
        case 'nousername': return computed(_ => t('error.nousername')) 
        case 'nopassword': return computed(_ => t('error.nopassword')) 
        case 'invalid': return computed(_ => t('error.invalid')) 
        case 'noaccess': return computed(_ => t('error.noaccess')) 
        case 'unknown': return computed(_ => t('error.unknown')) 
    }
}

const username = computed<string>(() =>
{
    return store.username.value;
});


const usernameValid = computed<boolean>(() =>
{
    return username.value.trim() != '';
});

        
const passwordValid = computed<boolean>(() =>
{            
    return password.value.trim() != '';
});



onMounted(() =>
{
    document.title = t('title');
    watch(i18n.locale, () => { document.title = t('title'); });


    if (!!route.query.c)
    {
        store.readInputJwt(route.query.c.toString());

        const jwtDecoded = store.getJwtDecoded();
        if (!!jwtDecoded && !!jwtDecoded.lng)
        {
            // De taal overnemen van de binnenkomende JWT. Deze is nog in LEF stijl (nlNL), omzetten naar ISO (nl-NL)
            const lng = jwtDecoded.lng.length == 4
                        ? jwtDecoded.lng.substring(0, 2) + '-' + jwtDecoded.lng.substring(2)
                        : jwtDecoded.lng;

            globalI18n.locale.value = getBestMatchingLocale(lng);
        }
    }

    
    validateExisting();
});


function setUsername(event: Event)
{
    const value = (event.target as HTMLInputElement).value;
    store.username.value = value;
}


async function validateExisting()
{
    const jwt = store.getJwtString();
    if (jwt !== null)
    {
        const response = await axios.post<LoginValidateResponse>('/v2/login/validate', 
            {
                JWT: jwt,
                App: store.app.value,
                AppSwitch: store.appSwitch.value
            } as LoginValidateRequest);

        if (response.status == 200)
        {
            store.applyGrantedApp(response.data.App);
            loginSuccess(response.data.Renewed, response.data.ACSUrl);
            return;
        }

        // Geen foutmelding tonen bij binnenkomst, simpelweg het loginscherm.
        // Wel de ?c= parameter weghalen.
        router.replace({ name: 'login', query: { r: store.returnUrl.value, app: store.app.value } });
    }

    validatingExisting.value = false;

    nextTick(() =>
    {
        if (!!username.value)
            passwordInput.value?.focus();
        else
            usernameInput.value?.focus();
    });
}


async function validateLogin()
{
    attemptedLogin.value = true;

    if (!usernameValid.value && !passwordValid.value)
    {
        showMessage('nousernamepassword');
        usernameInput.value?.focus();
        return;
    }

    if (!usernameValid.value)
    {
        showMessage('nousername');
        usernameInput.value?.focus();
        return;
    }

    if (!passwordValid.value)
    {
        showMessage('nopassword');
        passwordInput.value?.focus();
        return;
    }

    clearMessage();
    let success = false;
    validating.value = true;

    try
    {
        const response = await axios.post<LoginResponse>('/v2/login', {
                Username: username.value,
                Password: password.value,
                App: store.app.value,
                Locale: i18n.locale.value
            } as LoginRequest);
        
        switch (response.status)
        {
            case 200:
                if (response.data.CheckMFA) {
                    store.mfaJWT.value = response.data.JWT
                    router.push({ name: 'mfa' });
                    return;
                }
                if (response.data.WachtwoordVerlopen) {
                    passwordExpired.value = true;
                    return;
                }
                if (response.data.WachtwoordOngeldig) {
                    passwordInvalidated.value = true;
                    return;
                }
                store.applyGrantedApp(response.data.App);
                loginSuccess(response.data.JWT, response.data.ACSUrl);
                success = true;
                break;

            case 401:
                showMessage('invalid');

                // Wachtwoord is het meest waarschijnlijke
                passwordInput.value?.focus();
                break;

            case 403:
                showMessage('noaccess');
                break;

            default:
                showMessage('unknown');
                break;
        }
    }
    finally
    {
        if (!success)
            validating.value = false;
    }
}


function loginSuccess(jwt: string | undefined, acsUrl: string)
{
    acsPostData.value = {
        action: acsUrl,
        jwt: jwt || store.getJwtString()
    };
    
    nextTick(() => {
        acsForm.value?.submit();
    });
}


function clearMessage()
{
    errorMessage.value = null;
}


function showMessage(value: string)
{
    errorMessage.value = value;
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(Bubble, null, {
    default: _withCtx(() => [
      (validatingExisting.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(Loader, { style: 'white' })
          ]))
        : (passwordExpired.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("p", null, _toDisplayString(_unref(t)('error.passwordExpiredExplanation')), 1),
              _createElementVNode("p", null, _toDisplayString(_unref(t)('error.passwordEmailSent')), 1),
              _createElementVNode("button", {
                onClick: _cache[0] || (_cache[0] = ($event: any) => (passwordExpired.value = false))
              }, "Terug naar inloggen")
            ]))
          : (passwordInvalidated.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("p", null, _toDisplayString(_unref(t)('error.passwordInvalidatedExplanation')), 1),
                _createElementVNode("p", null, _toDisplayString(_unref(t)('error.passwordEmailSent')), 1),
                _createElementVNode("button", {
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (passwordInvalidated.value = false))
                }, "Terug naar inloggen")
              ]))
            : (_openBlock(), _createElementBlock("form", {
                key: 3,
                class: "login",
                onSubmit: _withModifiers(validateLogin, ["prevent"])
              }, [
                _createElementVNode("input", {
                  class: _normalizeClass(["form-control", { error: attemptedLogin.value && !usernameValid.value }]),
                  id: "username",
                  disabled: validating.value,
                  placeholder: _unref(t)('placeholders.username'),
                  type: "text",
                  ref_key: "usernameInput",
                  ref: usernameInput,
                  value: username.value,
                  onInput: setUsername,
                  autofocus: "",
                  autocomplete: "username"
                }, null, 42, _hoisted_4),
                _withDirectives(_createElementVNode("input", {
                  class: _normalizeClass(["form-control", { error: attemptedLogin.value && !passwordValid.value }]),
                  id: "password",
                  disabled: validating.value,
                  placeholder: _unref(t)('placeholders.password'),
                  type: "password",
                  ref_key: "passwordInput",
                  ref: passwordInput,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                  autocomplete: "current-password"
                }, null, 10, _hoisted_5), [
                  [_vModelText, password.value]
                ]),
                _createElementVNode("input", {
                  type: "submit",
                  id: "login",
                  class: "btn",
                  value: _unref(t)('submit'),
                  disabled: validating.value
                }, null, 8, _hoisted_6),
                _createElementVNode("div", _hoisted_7, [
                  (validating.value && errorMessage.value === null)
                    ? (_openBlock(), _createBlock(Loader, {
                        key: 0,
                        style: 'white'
                      }))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(translatedMessage.value?.value), 1)
                ]),
                _createVNode(_component_router_link, {
                  to: { name: 'resetpassword' },
                  class: "resetpassword"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('forgotpassword')), 1)
                  ]),
                  _: 1
                })
              ], 32)),
      _createElementVNode("form", {
        style: {"display":"none"},
        method: "POST",
        action: acsPostData.value.action,
        ref_key: "acsForm",
        ref: acsForm
      }, [
        _createElementVNode("input", {
          type: "hidden",
          name: "IssuedFrom",
          value: _unref(store).app.value
        }, null, 8, _hoisted_9),
        _createElementVNode("input", {
          type: "hidden",
          name: "ReturnUrl",
          value: _unref(store).returnUrl.value
        }, null, 8, _hoisted_10),
        _createElementVNode("input", {
          type: "hidden",
          name: "JWT",
          value: acsPostData.value.jwt
        }, null, 8, _hoisted_11)
      ], 8, _hoisted_8)
    ]),
    _: 1
  }))
}
}

})